
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Check, Hospital, Plus, Search } from "lucide-react";
import { isDevelopment } from "@/utils/environmentUtils";
import { initiateFHIROAuth } from "@/utils/fhirUtils";

interface HealthSystem {
  id: string;
  name: string;
  description?: string;
  resourceType?: string;
  status?: string;
  connectionType?: {
    system: string;
    code: string;
  };
  managingOrganization?: {
    reference: string;
  };
  period?: {
    start: string;
  };
  payloadType?: Array<{
    coding: Array<{
      system: string;
      code: string;
    }>;
  }>;
  payloadMimeType?: string[];
  address?: string;
  contained?: Array<{
    resourceType: string;
    id: string;
    name: string;
    address?: Array<{
      line: string[];
      city: string;
      state: string;
      postalCode: string;
      country: string;
    }>;
  }>;
}

// Mock API function for development
const searchHealthSystemsMock = async (
  query: string
): Promise<HealthSystem[]> => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 300));

  // Mock data for health systems only
  const mockSystems: HealthSystem[] = [
    {
      resourceType: "Endpoint",
      id: "e58d5dc3-325e-ee11-9162-001dd8b71f1a",
      contained: [
        {
          resourceType: "Organization",
          id: "u7YeCyrx9wwq2Wx3rjca2rKTPOpGYYYTTrIAMaR2+hE=",
          name: "Mayo Clinic",
        },
      ],
      status: "active",
      connectionType: {
        system:
          "http://terminology.hl7.org/CodeSystem/endpoint-connection-type",
        code: "hl7-fhir-rest",
      },
      name: "Mayo Clinic",
      managingOrganization: {
        reference: "u7YeCyrx9wwq2Wx3rjca2rKTPOpGYYYTTrIAMaR2+hE=",
      },
      period: {
        start: "2024-11-08T00:00:00-06:00",
      },
      payloadType: [
        {
          coding: [
            {
              system:
                "http://hl7.org/fhir/us/davinci-pdex-plan-net/CodeSystem/EndpointPayloadTypeCS",
              code: "NA",
            },
          ],
        },
      ],
      payloadMimeType: [
        "application/fhir+json",
        "application/json+fhir",
        "application/xml+fhir",
        "application/fhir+xml",
      ],
      address: "https://mcc.api.mayo.edu/epic-fhir/api/FHIR/R4/",
    },
    {
      resourceType: "Endpoint",
      id: "bf43c2aa-b413-eb11-9130-001dd8b71f1a",
      contained: [
        {
          resourceType: "Organization",
          id: "YmtZ04fJcMRBbEnQAQGdE0isf4BC8QW0IBJHKHabLbo=",
          name: "Childrens's Healthcare of Atlanta",
        },
      ],
      status: "active",
      connectionType: {
        system:
          "http://terminology.hl7.org/CodeSystem/endpoint-connection-type",
        code: "hl7-fhir-rest",
      },
      name: "Childrens's Healthcare of Atlanta",
      managingOrganization: {
        reference: "YmtZ04fJcMRBbEnQAQGdE0isf4BC8QW0IBJHKHabLbo=",
      },
      period: {
        start: "2021-11-05T00:00:00-05:00",
      },
      payloadType: [
        {
          coding: [
            {
              system:
                "http://hl7.org/fhir/us/davinci-pdex-plan-net/CodeSystem/EndpointPayloadTypeCS",
              code: "NA",
            },
          ],
        },
      ],
      payloadMimeType: [
        "application/fhir+json",
        "application/json+fhir",
        "application/xml+fhir",
        "application/fhir+xml",
      ],
      address: "https://wpprod.choa.org/FHIR_PRD/api/FHIR/R4/",
    },
  ];

  if (!query || query.length < 2) return mockSystems.slice(0, 5);

  return mockSystems.filter(
    (system) =>
      system.name.toLowerCase().includes(query.toLowerCase()) ||
      system.description?.toLowerCase().includes(query.toLowerCase())
  );
};

// Production API function
const searchHealthSystemsProduction = async (
  query: string
): Promise<HealthSystem[]> => {
  if (!query || query.length < 2) return [];

  try {
    const response = await fetch(
      `/api/health_systems?q=${encodeURIComponent(query)}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching health systems:", error);
    return [];
  }
};

// Main search function that switches based on environment
const searchHealthSystems = async (query: string): Promise<HealthSystem[]> => {
  console.log("Using production API for health systems search");
  return searchHealthSystemsProduction(query);
};

interface AddDataSourceDialogProps {
  onAddSource: (source: HealthSystem) => void;
}

export const AddDataSourceDialog = ({
  onAddSource,
}: AddDataSourceDialogProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<HealthSystem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSource, setSelectedSource] = useState<HealthSystem | null>(
    null
  );
  const [isImporting, setIsImporting] = useState(false);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    setSelectedSource(null); // Clear selection when typing

    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchHealthSystems(query);
      setSearchResults(results);
    } catch (error) {
      console.error("Error searching health systems:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSource = (source: HealthSystem) => {
    setSelectedSource(source);
    setSearchQuery(source.name);
    setSearchResults([]); // Clear results after selection
  };

  const handleImportRecords = async () => {
    if (!selectedSource) return;

    setIsImporting(true);

    try {
      console.log("Starting medical records import for:", selectedSource.name);

      // First, add the source to the UI
      onAddSource(selectedSource);

      // Convert HealthSystem to HealthSystemEndpoint format for FHIR OAuth
      const endpoint = {
        resourceType: selectedSource.resourceType || "Endpoint",
        status: selectedSource.status || "active",
        contained: selectedSource.contained || [],
        name: selectedSource.name,
        id: selectedSource.id,
        address:
          selectedSource.address ||
          `https://edge.zpaper.com/r/fhir/example.com/fhir/r4/${selectedSource.id}`,
      };

      // Then initiate the FHIR OAuth flow
      await initiateFHIROAuth(endpoint);

      // Note: The user will be redirected to the OAuth provider
      // The dialog will remain open until the redirect happens
    } catch (error) {
      console.error("Error importing medical records:", error);
      setIsImporting(false);
      // You might want to show an error toast here
    }
  };

  const handleDialogOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset form when closing
      setSearchQuery("");
      setSelectedSource(null);
      setSearchResults([]);
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogTrigger asChild>
        <Button className="flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>Add Health System</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Health System</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Search for your health system
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Type to search (e.g., Mayo Clinic, Kaiser)..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
                disabled={isImporting}
              />
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="border rounded-md max-h-48 overflow-y-auto">
              {searchResults.map((source) => (
                <div
                  key={source.id}
                  className="flex items-center space-x-3 p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                  onClick={() => handleSelectSource(source)}
                >
                  <Hospital className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm break-words line-clamp-2">{source.name}</div>
                    {source.description && (
                      <div className="text-xs text-muted-foreground break-words line-clamp-1">
                        {source.description}
                      </div>
                    )}
                  </div>
                  {selectedSource?.id === source.id && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Loading State */}
          {isSearching && (
            <div className="text-center py-4 text-muted-foreground">
              Searching health systems...
            </div>
          )}

          {/* No Results */}
          {searchQuery.length >= 2 &&
            !isSearching &&
            searchResults.length === 0 &&
            !selectedSource && (
              <div className="text-center py-4 text-muted-foreground">
                No health systems found for "{searchQuery}"
              </div>
            )}

          {/* Selected Source Display */}
          {selectedSource && (
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-md">
              <div className="flex items-center space-x-3">
                <Hospital className="h-5 w-5 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-primary text-sm break-words">
                    {selectedSource.name}
                  </div>
                  {selectedSource.description && (
                    <div className="text-xs text-muted-foreground break-words">
                      {selectedSource.description}
                    </div>
                  )}
                </div>
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
              disabled={isImporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImportRecords}
              disabled={!selectedSource || isImporting}
            >
              {isImporting ? "Connecting..." : "Import Medical Records"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
